import { useEffect, useState } from 'react';
import { Clock, AlertTriangle, Trophy, CheckCircle2, Zap } from 'lucide-react';

/**
 * Task timeline + live countdown + auto-computed grade badge.
 *
 * Grading buckets (mirror backend `_grade_task`):
 *   A = completed in first third of the window (early)
 *   B = middle third (on track)
 *   C = last third (just in time)
 *   D = after deadline (late)
 *
 * The parent passes the task object with fields:
 *   started_at, created_at, deadline_iso, last_completed_at, status, grade
 */
export const GRADE_META = {
  A: { label: 'A · Excellent',         cls: 'bg-emerald-500 text-white border-emerald-600', icon: Trophy,        blurb: '90-100% — done in the early part of the window' },
  B: { label: 'B · Good',              cls: 'bg-cyan-500   text-white border-cyan-600',     icon: CheckCircle2,  blurb: '80-89% — done in the first half' },
  C: { label: 'C · Satisfactory',      cls: 'bg-sky-500    text-white border-sky-600',      icon: Zap,           blurb: '70-79% — done in the third quarter' },
  D: { label: 'D · Needs Improvement', cls: 'bg-amber-500  text-white border-amber-600',    icon: AlertTriangle, blurb: '60-69% — cut it close to the deadline' },
  F: { label: 'F · Unsatisfactory',    cls: 'bg-rose-600   text-white border-rose-700',     icon: AlertTriangle, blurb: '0-59% — completed after the deadline' },
};

function fmtHMS(ms) {
  if (ms == null || isNaN(ms)) return '—';
  const abs = Math.max(0, Math.abs(ms));
  const s = Math.floor(abs / 1000) % 60;
  const m = Math.floor(abs / 60000) % 60;
  const h = Math.floor(abs / 3600000) % 24;
  const d = Math.floor(abs / (24 * 3600000));
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

export default function TaskTimeline({ task, compact = false }) {
  const [now, setNow] = useState(Date.now());

  const startedAt = task?.started_at || task?.created_at;
  const deadlineIso = task?.deadline_iso;
  const completedAt = task?.last_completed_at;
  const isDone = task?.status === 'done';

  // Live tick only while task is running and there's a deadline
  useEffect(() => {
    if (isDone || !deadlineIso) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [isDone, deadlineIso]);

  if (!startedAt) return null;

  const startMs = new Date(startedAt).getTime();
  const deadlineMs = deadlineIso ? new Date(deadlineIso).getTime() : null;

  // If done, show grade badge
  if (isDone && task?.grade) {
    const meta = GRADE_META[task.grade];
    if (!meta) return null;
    const GradeIcon = meta.icon;
    const completedMs = completedAt ? new Date(completedAt).getTime() : now;
    const timeTaken = completedMs - startMs;
    return (
      <div className="flex items-center gap-2 flex-wrap mt-1" data-testid={`task-grade-${task.id}`}>
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold border rounded px-2 py-0.5 ${meta.cls}`}
          title={meta.blurb}
        >
          <GradeIcon className="h-3 w-3" /> {meta.label}
          {task.grade_score != null && (
            <span className="ml-1 opacity-90">· {task.grade_score}%</span>
          )}
        </span>
        {!compact && (
          <span className="text-[10px] text-muted-foreground">
            took {fmtHMS(timeTaken)}
          </span>
        )}
      </div>
    );
  }

  // Done without grade (no deadline was set)
  if (isDone) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-emerald-600 mt-1" data-testid={`task-grade-${task.id}`}>
        <CheckCircle2 className="h-3 w-3" /> Completed (no deadline)
      </div>
    );
  }

  // Not done and no deadline — nothing to show
  if (!deadlineMs) return null;

  // Live timer + progress
  const totalWindow = deadlineMs - startMs;
  const elapsed = now - startMs;
  const remaining = deadlineMs - now;
  const overdue = remaining < 0;
  const pct = totalWindow > 0 ? Math.max(0, Math.min(100, (elapsed / totalWindow) * 100)) : (overdue ? 100 : 0);

  // Live projection — which grade would they earn RIGHT NOW?
  let barCls = 'bg-emerald-500';
  let stateLabel = 'Excellent zone';
  let stateCls = 'text-emerald-600';
  if (overdue) {
    barCls = 'bg-rose-600';
    stateLabel = 'Overdue — F';
    stateCls = 'text-rose-600';
  } else if (pct >= 75) {
    barCls = 'bg-amber-500';
    stateLabel = 'Needs Improvement zone';
    stateCls = 'text-amber-600';
  } else if (pct >= 50) {
    barCls = 'bg-sky-500';
    stateLabel = 'Satisfactory zone';
    stateCls = 'text-sky-600';
  } else if (pct >= 25) {
    barCls = 'bg-cyan-500';
    stateLabel = 'Good zone';
    stateCls = 'text-cyan-600';
  } else {
    barCls = 'bg-emerald-500';
    stateLabel = 'Excellent zone — finish now for an A';
    stateCls = 'text-emerald-600';
  }

  const fmtTime = (ms) => new Date(ms).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div className={`mt-1 space-y-1 ${compact ? '' : 'w-full'}`} data-testid={`task-timeline-${task.id}`}>
      <div className="flex items-center justify-between text-[10px]">
        <span className={`inline-flex items-center gap-1 font-semibold ${stateCls}`}>
          <Clock className="h-3 w-3" />
          {overdue ? `${fmtHMS(-remaining)} overdue` : `${fmtHMS(remaining)} left`}
        </span>
        {!compact && <span className="text-muted-foreground">{stateLabel}</span>}
      </div>
      <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
        <div
          className={`h-full ${barCls} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid={`task-progress-${task.id}`}
        />
      </div>
      <div className="flex items-center justify-between text-[9px] text-muted-foreground">
        <span title="Task start" data-testid={`task-start-${task.id}`}>
          Start: {fmtTime(startMs)}
        </span>
        <span title="Task end / deadline" data-testid={`task-end-${task.id}`}>
          End: {fmtTime(deadlineMs)}
        </span>
      </div>
    </div>
  );
}
